import React, { useState } from 'react';
import {
  Button,
  colors,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getBranches } from 'api/branchApi';
import { listClients } from 'api/clientsApi';
import { useRateForm } from '../useRateForm';
import EquipmentRateFormFields from './EquipmentRateFormFields';
import {
  EquipmentRateFormType,
  listCategoryData,
  listEquipmentItems,
  updateRate,
} from 'api/equipmentApi';
import {
  checkEquipmentDuplication,
  filterEquipmentRates,
} from '../Utils/Utils';
import { useSnackbar } from 'notistack';
import { RateType } from 'api/consumablesApi';
import LoadingSpinner from 'components/Spinners/LoadingSpinner';

interface Props {
  openSellRateForm: boolean;
  openBuyRateForm: boolean;
  handleSellRateFormClose: () => void;
  handleBuyRateFormClose: () => void;
  editRateData?: EquipmentRateFormType;
}

function AddEquipmentRates({
  openSellRateForm,
  openBuyRateForm,
  handleSellRateFormClose,
  handleBuyRateFormClose,
  editRateData,
}: Props) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [rateId, setRateId] = useState('');
  const [formData, setFormData] = useState<EquipmentRateFormType | null>(null);
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      equipmentCategoryId: '',
      branch: null,
      clientId: '',
      sellRate: '',
      buyRate: '',
      isDefault: false,
      effectiveFrom: new Date().toISOString(),
    },
  });
  const {
    equipmentSellRateMutate,
    isAddingEquipmentSellRate,
    equipmentBuyRateMutate,
    isAddingEquipmentBuyRate,
  } = useRateForm(
    openBuyRateForm ? handleBuyRateFormClose : handleSellRateFormClose
  );

  const { mutate: updateRateMutate, isLoading: isUpdatingRate } = useMutation(
    updateRate,
    {
      onSuccess: async () => {
        enqueueSnackbar('Successfully updated rate', { variant: 'success' });
        await queryClient.invalidateQueries(['equipment-rate-items']);
        setFormData(null);
        openBuyRateForm ? handleBuyRateFormClose() : handleSellRateFormClose();
      },
      onError: () => {
        enqueueSnackbar('Failed to update rate', { variant: 'error' });
      },
    }
  );

  const { data: branches } = useQuery(['branches'], getBranches);
  const equipmentList = useQuery(['equipment-rate-items'], listEquipmentItems);
  const { data: clients } = useQuery(['list-clients'], listClients, {
    staleTime: 30 * 60 * 1000,
  });

  const { data: equipmentCategories } = useQuery(
    ['list-categories'],
    listCategoryData
  );

  const { equipmentCategoryId, clientId, branch } = watch([
    'equipmentCategoryId',
    'clientId',
    'branch',
  ]);

  React.useMemo(() => {
    if (!equipmentList.data) return [];
    return equipmentList.data.map((item) => ({
      id: item?.id,
      name: item?.name,
    }));
  }, [equipmentList.data]);

  const buyTableRows = React.useMemo(() => {
    return filterEquipmentRates(
      equipmentList,
      {
        equipmentCategoryId,
        clientId,
        branchId: branch?.id,
      },
      'buyRates'
    );
  }, [equipmentList, equipmentCategoryId, clientId, branch]);
  const sellTableRows = React.useMemo(() => {
    const filteredData = filterEquipmentRates(
      equipmentList,
      {
        equipmentCategoryId,
        clientId,
        branchId: branch?.id,
      },
      'sellRates'
    );

    return filteredData;
  }, [equipmentList, equipmentCategoryId, clientId, branch]);

  const onSubmit = (data: EquipmentRateFormType) => {
    setFormData(data);
    const { checkRateDuplicate, foundRateId } = checkEquipmentDuplication(
      data,
      equipmentList.data
    );
    if (checkRateDuplicate) {
      setRateId(foundRateId);
      setOpenConfirmationDialog(true);
      return;
    } else {
      addNewRate(data);
    }
  };

  const addNewRate = (data: EquipmentRateFormType) => {
    if (openBuyRateForm) {
      equipmentBuyRateMutate({ data });
    } else if (openSellRateForm) {
      equipmentSellRateMutate({ data });
    }
  };

  const updateExistingRate = () => {
    const data = formData;
    if (openBuyRateForm) {
      updateRateMutate({
        id: rateId,
        data: data,
        rateType: RateType.BuyRate,
      });
    } else if (openSellRateForm) {
      updateRateMutate({
        id: rateId,
        data: data,
        rateType: RateType.SellRate,
      });
    }
  };

  return (
    <Dialog open={openBuyRateForm || openSellRateForm} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ paddingY: '1.5rem' }}>
        Add {openBuyRateForm ? 'Buy' : 'Sell'} Rate
      </DialogTitle>
      <Divider />
      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <EquipmentRateFormFields
            control={control}
            errors={errors}
            isEditMode={!!editRateData}
            openBuyRateForm={openBuyRateForm}
            watch={watch}
            equipmentItemNames={equipmentCategories || []}
            filteredBranches={branches ? branches : []}
            openSellRateForm={openSellRateForm}
            clients={clients}
            sellTableRows={sellTableRows}
            buyTableRows={buyTableRows}
            setValue={setValue}
          />
        </form>
      </DialogContent>
      <Dialog
        open={openConfirmationDialog}
        onClose={() => setOpenConfirmationDialog(false)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>Override Existing Rate</DialogTitle>
        <Divider />
        <DialogContent>
          <Typography sx={{ marginTop: '1rem', marginBottom: '2rem' }}>
            You already have a rate for the selected equipment on the effective
            date. Do you want to override the existing rate or cancel to modify
            the effective date?
          </Typography>
          <Divider sx={{ marginY: '1rem' }} />
          <DialogActions sx={{ marginBottom: '0rem' }}>
            <Stack
              spacing={1.5}
              direction="row"
              sx={{ justifyContent: 'flex-end' }}>
              <Button
                onClick={() => setOpenConfirmationDialog(false)}
                sx={{ color: colors.grey[600] }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                sx={{
                  paddingX: '3rem',
                }}
                disabled={isUpdatingRate}
                startIcon={isUpdatingRate ? <LoadingSpinner /> : undefined}
                onClick={() => {
                  updateExistingRate();
                  setOpenConfirmationDialog(false);
                }}>
                Override Rate
              </Button>
            </Stack>
          </DialogActions>
        </DialogContent>
      </Dialog>
      <Divider />
      <DialogActions sx={{ padding: '1.5rem' }}>
        <Button
          onClick={
            openBuyRateForm ? handleBuyRateFormClose : handleSellRateFormClose
          }
          sx={{ color: colors.grey[700], mr: 2 }}
          size="large">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          disabled={
            isAddingEquipmentSellRate ||
            isAddingEquipmentBuyRate ||
            isUpdatingRate
          }
          startIcon={
            isAddingEquipmentSellRate ||
            isAddingEquipmentBuyRate ||
            isUpdatingRate ? (
              <LoadingSpinner />
            ) : undefined
          }
          size="large">
          Add {openBuyRateForm ? 'Buy' : 'Sell'} Rate
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddEquipmentRates;
